'use client';

import { useAccountInfo } from '@/hooks/query/account';
import { useAuthToken } from '@/store/auth';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { Button } from '@nf/ui';
import { Icon } from '@/components/icons/svg-icons';
import { cva, VariantProps } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { KafkaNote } from '@nf/utils-common/constants';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { aIChatToggleRef } from '../ai-chat';

const aiChatButtonVariants = cva('ai-chat-button', {
	variants: {
		variant: {
			'header': 'ai-chat-button--header',
			'odds-page-mars': 'ai-chat-button--odds-page-mars',
			'odds-page-galaxy': 'ai-chat-button--odds-page-galaxy',
		},
		defaultVariants: {
			variant: 'header',
		},
	},
});

interface AIChatButtonProps {
	variant: VariantProps<typeof aiChatButtonVariants>['variant'];
}

export const AIChatButton = ({
	variant = 'header'
}: AIChatButtonProps) => {
	const t = useTranslations('General');
	const view = useAppearanceStore(state => state.view);

	const { user } = useAccountInfo();
	const authToken = useAuthToken();
	const { enableAIChat } = useLicSettings();

	const onClick = async () => {
		if (!aIChatToggleRef.current) return;
		await aIChatToggleRef.current();
		setKafkaNormal({
			note: KafkaNote.AIChat,
			type: 1,
			action: view === ViewEnum.galaxy ? 2 : 1
		});
	};

	return user?.ID && authToken && enableAIChat ? (
		<Button
			variant="classic"
			className={aiChatButtonVariants({ variant })}
			shape={'round'}
			onClick={onClick}
		>
			<Icon name="chat-bot" />
			<span className="ai-chat-button__text">{t('lbl_AIChat')}</span>
		</Button>
	) : null;
};